/**
 * Avaliador das fórmulas da calculadora gerada pelo modelo. Parser próprio
 * (descida recursiva), sem eval/Function: só números, as variáveis das
 * entradas, operadores aritméticos e de comparação, e as funções da lista.
 * Qualquer outra coisa é erro — o texto vem do modelo, não é confiável.
 */

type Token =
  | { t: 'num'; v: number }
  | { t: 'id'; v: string }
  | { t: 'op'; v: string };

const FUNCS: Record<string, (...a: number[]) => number> = {
  min: (...a) => Math.min(...a),
  max: (...a) => Math.max(...a),
  abs: (x) => Math.abs(x),
  raiz: (x) => Math.sqrt(x),
  sqrt: (x) => Math.sqrt(x),
  piso: (x) => Math.floor(x),
  floor: (x) => Math.floor(x),
  teto: (x) => Math.ceil(x),
  ceil: (x) => Math.ceil(x),
  arred: (x, n = 0) => { const f = 10 ** n; return Math.round(x * f) / f; },
  round: (x, n = 0) => { const f = 10 ** n; return Math.round(x * f) / f; },
};

const OPS = ['<=', '>=', '==', '!=', '&&', '||', '+', '-', '*', '/', '^', '%', '(', ')', ',', '<', '>'];

function tokenize(src: string): Token[] {
  const out: Token[] = [];
  let i = 0;
  while (i < src.length) {
    const c = src[i];
    if (/\s/.test(c)) { i += 1; continue; }
    const num = /^(\d+([.,]\d+)?|[.,]\d+)/.exec(src.slice(i));
    if (num) {
      out.push({ t: 'num', v: parseFloat(num[0].replace(',', '.')) });
      i += num[0].length;
      continue;
    }
    const id = /^[A-Za-z_À-ÿ][A-Za-z0-9_À-ÿ]*/.exec(src.slice(i));
    if (id) { out.push({ t: 'id', v: id[0] }); i += id[0].length; continue; }
    const op = OPS.find((o) => src.startsWith(o, i));
    if (!op) throw new Error(`caractere inesperado: ${c}`);
    out.push({ t: 'op', v: op });
    i += op.length;
  }
  return out;
}

/** Avalia `formula` com os valores das entradas. Lança erro se inválida. */
export function evaluateFormula(formula: string, vars: Record<string, number>): number {
  const toks = tokenize(String(formula || ''));
  let p = 0;
  const peek = () => toks[p];
  const isOp = (v: string) => peek()?.t === 'op' && peek()!.v === v;
  const expect = (v: string) => {
    if (!isOp(v)) throw new Error(`esperava ${v}`);
    p += 1;
  };

  // Precedência, da menor para a maior: ou, e, comparação, + -, * / %, ^, unário.
  const or = (): number => {
    let a = and();
    while (isOp('||') || (peek()?.t === 'id' && peek()!.v === 'ou')) { p += 1; const b = and(); a = a || b ? 1 : 0; }
    return a;
  };
  const and = (): number => {
    let a = cmp();
    while (isOp('&&') || (peek()?.t === 'id' && peek()!.v === 'e')) { p += 1; const b = cmp(); a = a && b ? 1 : 0; }
    return a;
  };
  const cmp = (): number => {
    let a = add();
    for (;;) {
      const tk = peek();
      if (tk?.t !== 'op' || !['<', '<=', '>', '>=', '==', '!='].includes(tk.v)) return a;
      p += 1;
      const b = add();
      a = ({ '<': a < b, '<=': a <= b, '>': a > b, '>=': a >= b, '==': a === b, '!=': a !== b } as any)[tk.v] ? 1 : 0;
    }
  };
  const add = (): number => {
    let a = mul();
    while (isOp('+') || isOp('-')) { const o = toks[p++].v; const b = mul(); a = o === '+' ? a + b : a - b; }
    return a;
  };
  const mul = (): number => {
    let a = pow();
    while (isOp('*') || isOp('/') || isOp('%')) {
      const o = toks[p++].v;
      const b = pow();
      a = o === '*' ? a * b : o === '/' ? a / b : a % b;
    }
    return a;
  };
  const pow = (): number => {
    const a = unary();
    if (isOp('^')) { p += 1; return a ** pow(); } // associa à direita
    return a;
  };
  const unary = (): number => {
    if (isOp('-')) { p += 1; return -unary(); }
    if (isOp('+')) { p += 1; return unary(); }
    return atom();
  };
  const atom = (): number => {
    const tk = peek();
    if (!tk) throw new Error('fórmula incompleta');
    if (tk.t === 'num') { p += 1; return tk.v; }
    if (tk.t === 'op' && tk.v === '(') { p += 1; const v = or(); expect(')'); return v; }
    if (tk.t === 'id') {
      p += 1;
      const name = tk.v;
      if (isOp('(')) {
        p += 1;
        const args: number[] = [];
        if (!isOp(')')) {
          args.push(or());
          while (isOp(',')) { p += 1; args.push(or()); }
        }
        expect(')');
        if (name === 'se' || name === 'if') {
          if (args.length !== 3) throw new Error('se(condição, sim, não)');
          return args[0] ? args[1] : args[2];
        }
        const fn = Object.prototype.hasOwnProperty.call(FUNCS, name) ? FUNCS[name] : undefined;
        if (!fn) throw new Error(`função desconhecida: ${name}`);
        return fn(...args);
      }
      if (!Object.prototype.hasOwnProperty.call(vars, name)) throw new Error(`variável desconhecida: ${name}`);
      return Number(vars[name]);
    }
    throw new Error(`token inesperado: ${tk.v}`);
  };

  const value = or();
  if (p !== toks.length) throw new Error('sobrou texto na fórmula');
  return value;
}
